import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/site/Motion";
import { Label, Section, H2, Paras, CtaButton } from "@/components/site/Blocks";

const AI = [
  { t: "AI Search Optimisation", d: "Improve the information, structure and authority signals that help AI-powered search systems understand your business.", to: "/ai-search-visibility" },
  { t: "Answer Engine Optimisation", d: "Optimise content so that it is easier for answer-driven search systems to interpret and use when responding to relevant questions.", to: "/aeo" },
  { t: "Generative Engine Optimisation", d: "Strengthen your brand's potential visibility across generative AI search experiences and AI-generated recommendations.", to: "/geo" },
];

export const AiSearch = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} data-testid="home-ai-section" className="grain relative bg-navy py-24 text-white sm:py-32">
      <div className="wrap">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Label n="03" dark>AI Search</Label></Reveal>
            <Reveal delay={0.05}><H2 dark className="mt-6">AI Search <span className="font-serif-i text-gradient">Visibility</span></H2></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-serif-i text-2xl text-violet-200 sm:text-3xl">Search is expanding beyond the traditional results page.</p>
              <Paras dark className="mt-5" items={[
                "Platforms such as ChatGPT, Gemini, Perplexity and Google's AI-powered search experiences are changing how people discover and evaluate businesses.",
                "Perfect Digitals helps businesses understand and improve how they appear across this new search environment.",
              ]} />
            </Reveal>
            <Reveal delay={0.15} className="relative mt-10 aspect-[4/3] overflow-hidden rounded-3xl">
              <motion.img style={{ y, scale: 1.2 }} src="/images/network.jpg" alt="Neural network sculpture representing AI search" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_25%,rgba(10,18,51,0.75)_80%)]" />
            </Reveal>
          </div>
          <div data-testid="ai-search-grid" className="border-t border-white/15 lg:col-span-6 lg:col-start-7">
            {AI.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.05}>
                <Link to={a.to} data-testid={`ai-item-${i}`}
                  className="group grid w-full grid-cols-[auto_1fr_auto] items-start gap-5 border-b border-white/15 py-8 text-left transition-colors duration-500 hover:bg-white/[0.03] sm:gap-8 sm:px-4">
                  <span className="pt-2 font-mono text-xs text-violet-300">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block font-display text-2xl font-medium transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">{a.t}</span>
                    <span className="mt-3 block text-[15px] leading-relaxed text-slate-300">{a.d}</span>
                  </span>
                  <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-[background-color,border-color] duration-500 group-hover:border-violet-400 group-hover:bg-violet-500">
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
                  </span>
                </Link>
              </Reveal>
            ))}
            <Reveal className="mt-10"><CtaButton label="Explore AI Search Visibility" to="/ai-search-visibility" testid="explore-ai-cta" /></Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const PILLARS = ["Structured information", "Reputation", "Authority", "Reliable information", "Strong topical relevance", "Technically accessible content", "Clear website architecture"];

export const AiReady = ({ n = "04", intro, pillars = PILLARS, outro, cta }) => (
  <Section testid="ai-ready-section">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n}>Our philosophy</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">SEO First. <span className="font-serif-i text-gradient-dark">AI-Ready by Design.</span></H2></Reveal>
        <Reveal delay={0.1}><Paras className="mt-6" items={intro} /></Reveal>
        <Reveal delay={0.15}><p className="mt-6 font-display text-xl leading-snug text-slate-900">{outro}</p></Reveal>
        {cta && <Reveal delay={0.2} className="mt-8">{cta}</Reveal>}
      </div>
      <div className="lg:col-span-6 lg:col-start-7" data-testid="pillars-stack">
        <p className="mb-4 text-right font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">AI visibility ↑</p>
        <div className="space-y-2">
          {pillars.map((p, i) => (
            <Reveal key={p} delay={(pillars.length - i) * 0.06} y={20}
              style={{ marginLeft: `${(pillars.length - 1 - i) * 3}%`, marginRight: `${(pillars.length - 1 - i) * 3}%` }}
              className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 transition-[background-color,border-color] duration-300 hover:border-violet-500 hover:bg-violet-50 sm:px-6">
              <span className="font-display text-base text-slate-900 sm:text-lg">{p}</span>
              <span className="font-mono text-[11px] text-slate-500 group-hover:text-violet-700">{String(pillars.length - i).padStart(2, "0")}</span>
            </Reveal>
          ))}
        </div>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">SEO foundations</p>
      </div>
    </div>
  </Section>
);
